import { type CustomTypeResponse } from "@prismicio/editor-fields"
import { range } from "@prismicio/editor-support/Array"
import { caseInsensitiveIncludes } from "@prismicio/editor-support/String"
import {
  ActionList,
  ActionListItem,
  Box,
  Button,
  ConjoinedIcons,
  DefaultErrorMessage,
  Dialog,
  DialogContent,
  dialogErrorImage,
  ErrorBoundary,
  FormSearchInput,
  ScrollArea,
  Skeleton,
  Text,
  Tooltip
} from "@prismicio/editor-ui"
import { Suspense, useState } from "react"

import { useGoToNewDocument } from "../../app/routing"
import { useRepositoryTypes } from "../../app/services/customTypes"
import { useRole } from "../../app/services/user"
import { useDocumentListLocales } from "../useDocumentListLocales"

export interface CreateNewDocumentProps {
  setModalOpen: (isOpen: boolean) => void
  size?: "small" | "large"
  color?: "grey" | "purple"
}

export function CreateNewDocumentButton(props: CreateNewDocumentProps) {
  const { setModalOpen, color, size } = props

  return (
    <ErrorBoundary>
      <Suspense
        fallback={
          <Button startIcon="add" color={color} size={size} disabled>
            Create new
          </Button>
        }
      >
        <CreateNewDocument
          setModalOpen={setModalOpen}
          color={color}
          size={size}
        />
      </Suspense>
    </ErrorBoundary>
  )
}

function CreateNewDocument(props: CreateNewDocumentProps) {
  const { setModalOpen, color, size } = props

  const { locale } = useDocumentListLocales()
  const { isContributor } = useRole(locale)
  const userCanCreate = !isContributor

  return (
    <Tooltip
      visible={!userCanCreate}
      title="You can't create documents"
      content="Only users with the role of publisher or administrator can create documents."
    >
      <Button
        startIcon="add"
        color={color}
        size={size}
        disabled={!userCanCreate}
        onClick={() => setModalOpen(true)}
      >
        Create new
      </Button>
    </Tooltip>
  )
}

interface CreateNewDocumentModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  releaseId?: string
}

export function CreateNewDocumentModal(props: CreateNewDocumentModalProps) {
  const { open, onOpenChange, releaseId } = props
  const [searchTerm, setSearchTerm] = useState("")

  return (
    <Dialog
      size="small"
      hasClose={false}
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent>
        <Box
          flexDirection="column"
          maxHeight={460}
          height="100%"
          gap={4}
          padding={16}
        >
          <Box flexDirection="column" padding={{ bottom: 8 }}>
            <FormSearchInput value={searchTerm} onValueChange={setSearchTerm} />
          </Box>
          <ErrorBoundary renderError={() => <CustomTypesListError />}>
            <Text variant="smallBold" color="grey11">
              Pages
            </Text>
            <Suspense
              fallback={
                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  alignItems="center"
                  width="100%"
                  gap={8}
                >
                  {range(1, 6).map((num) => (
                    <Skeleton key={num} height={56} width="100%" />
                  ))}
                </Box>
              }
            >
              <CustomTypeOptions
                searchTerm={searchTerm}
                releaseId={releaseId}
              />
            </Suspense>
          </ErrorBoundary>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

interface CustomTypeOptionsProps {
  searchTerm: string
  releaseId?: string
}
function CustomTypeOptions(props: CustomTypeOptionsProps) {
  const { searchTerm, releaseId } = props
  const customTypes = useRepositoryTypes()

  const orderedCustomTypes = customTypes.sort((a, b) =>
    a.label.localeCompare(b.label)
  )

  const filteredCustomTypes = orderedCustomTypes.filter(
    (customType) =>
      caseInsensitiveIncludes(customType.label, searchTerm) && customType.status
  )

  return (
    <ScrollArea>
      <ActionList variant="borderless">
        {filteredCustomTypes.map((customType) => {
          return (
            <CustomTypeCard
              key={customType.id}
              releaseId={releaseId}
              customType={customType}
            />
          )
        })}
      </ActionList>
    </ScrollArea>
  )
}

function CustomTypesListError() {
  return (
    <Box
      height="100%"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
    >
      <DefaultErrorMessage
        title="Request failed"
        description="An error occurred while your custom types."
        errorImageSrc={dialogErrorImage}
      />
    </Box>
  )
}

interface CustomTypeCard {
  customType: CustomTypeResponse
  releaseId?: string
}

function CustomTypeCard(props: CustomTypeCard) {
  const { customType, releaseId } = props

  const { locale } = useDocumentListLocales()
  const goToNewDocument = useGoToNewDocument({ releaseId })

  return (
    <ActionListItem
      renderStartIcon={() => (
        <ConjoinedIcons largeIconName="database" smallIconName="add" />
      )}
      description={customType.repeatable ? "Repeatable" : "Singleton"}
      onClick={() =>
        goToNewDocument({
          customTypeId: customType.id,
          locale
        })
      }
    >
      {customType.label}
    </ActionListItem>
  )
}
